import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useStore from '../store/useStore';
import { kpisAPI, reportsAPI } from '../services/api';
import useModalOverlayClose from '../hooks/useModalOverlayClose';
import { formatKpiValue, slugify, CADENCE_LABEL } from '../utils/kpiFormat';
import '../styles/KpiManagement.css';

const CADENCES = ['day', 'week', 'month', 'quarter', 'year'];
const FORMATS = ['number', 'percent', 'currency', 'duration'];

const EMPTY = {
  key: '', name: '', department: '', description: '', sourceSystem: '', owner: '',
  measure: '', timeDimension: '', cadence: 'month', direction: 'higher', format: 'number', unit: '', timing: '',
  defaultTarget: '', targets: [],
};

const memberLabel = (m) => m.shortTitle || m.title || m.name.split('.').pop();

/**
 * Admin page: KPI definitions and targets, plus suggestions from the
 * templates the company's datasets are linked to.
 */
const KpiManagement = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentCompany = useStore((state) => state.currentCompany);
  const companyId = currentCompany?.id;

  const [kpis, setKpis] = useState([]);
  const [suggested, setSuggested] = useState([]);
  const [cubes, setCubes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [editing, setEditing] = useState(null); // form state or null
  const [saving, setSaving] = useState(false);

  const describeError = (err, fallback) => err?.response?.data?.error || err?.message || fallback;

  const load = useCallback(async () => {
    if (!companyId) return;
    try {
      setLoading(true);
      setError('');
      const [list, sugg, meta] = await Promise.all([
        kpisAPI.list(companyId),
        kpisAPI.suggested(companyId),
        reportsAPI.getMeta(companyId).catch(() => null),
      ]);
      if (list?.success) setKpis(list.data || []);
      if (sugg?.success) setSuggested(sugg.data || []);
      if (meta?.success) setCubes(meta.data?.cubes || []);
    } catch (err) {
      setError(describeError(err, 'Failed to load KPIs'));
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  // Deep link ?edit=<id> from the KPI page.
  useEffect(() => {
    const editId = searchParams.get('edit');
    if (editId && kpis.length) {
      const kpi = kpis.find((k) => String(k.id) === editId);
      if (kpi) openEdit(kpi);
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kpis, searchParams]);

  const openCreate = () => setEditing({ ...EMPTY, targets: [] });
  const openEdit = (kpi) => setEditing({
    ...EMPTY,
    ...kpi,
    timeDimension: kpi.timeDimension || '',
    timing: kpi.timing || '',
    department: kpi.department || '',
    description: kpi.description || '',
    sourceSystem: kpi.sourceSystem || '',
    owner: kpi.owner || '',
    unit: kpi.unit || '',
    defaultTarget: kpi.defaultTarget ?? '',
    targets: (kpi.targets || []).filter((t) => t.periodType !== 'default').map((t) => ({ periodStart: t.periodStart, target: t.target })),
  });

  const overlay = useModalOverlayClose(() => setEditing(null));

  const measureCube = editing?.measure ? editing.measure.split('.')[0] : '';
  const cube = cubes.find((c) => c.name === measureCube);
  const timeDims = (cube?.dimensions || []).filter((d) => d.type === 'time');

  const groups = useMemo(() => {
    const map = new Map();
    for (const k of kpis) {
      const dept = k.department || 'Other';
      if (!map.has(dept)) map.set(dept, []);
      map.get(dept).push(k);
    }
    return [...map.entries()];
  }, [kpis]);

  const update = (patch) => setEditing((prev) => ({ ...prev, ...patch }));

  const handleSave = async (e) => {
    e.preventDefault();
    if (!editing) return;
    setSaving(true);
    setError('');
    try {
      const payload = {
        key: editing.key || slugify(editing.name),
        name: editing.name,
        department: editing.department,
        description: editing.description,
        sourceSystem: editing.sourceSystem,
        owner: editing.owner,
        measure: editing.measure,
        timeDimension: editing.timeDimension || null,
        cadence: editing.cadence,
        direction: editing.direction,
        format: editing.format,
        unit: editing.unit,
        timing: editing.timing || null,
        defaultTarget: editing.defaultTarget === '' ? null : Number(editing.defaultTarget),
      };
      let saved;
      if (editing.id) {
        saved = await kpisAPI.update(editing.id, companyId, payload);
      } else {
        saved = await kpisAPI.create(companyId, payload);
      }
      if (!saved?.success) throw new Error(saved?.error || 'Save failed');
      const targets = [
        ...(payload.defaultTarget !== null ? [{ periodType: 'default', target: payload.defaultTarget }] : []),
        ...editing.targets.filter((t) => t.periodStart && t.target !== '').map((t) => ({ periodType: editing.cadence, periodStart: t.periodStart, target: Number(t.target) })),
      ];
      await kpisAPI.setTargets(saved.data.id, companyId, targets);
      setEditing(null);
      setMessage(`Saved "${payload.name}"`);
      await load();
    } catch (err) {
      setError(describeError(err, 'Failed to save KPI'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (kpi) => {
    if (!window.confirm(`Delete KPI "${kpi.name}"?`)) return;
    try {
      await kpisAPI.delete(kpi.id, companyId);
      setMessage(`Deleted "${kpi.name}"`);
      await load();
    } catch (err) {
      setError(describeError(err, 'Failed to delete KPI'));
    }
  };

  const handleAdopt = async (datasetName, keys) => {
    try {
      setError('');
      const res = await kpisAPI.adopt(companyId, datasetName, keys);
      if (!res?.success) throw new Error(res?.error || 'Adopt failed');
      setMessage(`Added ${res.data.length} KPI${res.data.length === 1 ? '' : 's'} from ${datasetName}`);
      await load();
    } catch (err) {
      setError(describeError(err, 'Failed to adopt KPIs'));
    }
  };

  if (loading) return <div className="kpi-management"><div className="loading-spinner" /></div>;

  const pendingSuggestions = suggested.filter((s) => !s.alreadyDefined);

  return (
    <div className="kpi-management">
      <div className="page-header">
        <div>
          <h2>KPIs</h2>
          {currentCompany && <p className="page-subtitle">Definitions and targets for: <strong>{currentCompany.name}</strong></p>}
        </div>
        <div className="page-actions">
          <button className="btn-secondary" onClick={() => navigate('/settings')}>Back to Settings</button>
          <button className="btn-primary" onClick={openCreate} disabled={!currentCompany}>+ New KPI</button>
        </div>
      </div>

      {error && <div className="error-message" style={{ marginBottom: '1rem' }}>{error}<button className="error-dismiss" onClick={() => setError('')} aria-label="Dismiss">×</button></div>}
      {message && <div className="success-message" style={{ marginBottom: '1rem' }}>{message}<button className="error-dismiss" onClick={() => setMessage('')} aria-label="Dismiss">×</button></div>}

      {pendingSuggestions.length > 0 && (
        <div className="kpi-suggestions">
          <h3>Suggested by your templates</h3>
          <p className="page-subtitle">Datasets linked to a template come with ready-made KPI definitions.</p>
          <ul>
            {pendingSuggestions.map((s) => (
              <li key={`${s.datasetName}-${s.key}`} className={s.available ? '' : 'unavailable'}>
                <div>
                  <strong>{s.name}</strong>
                  <span className="kpi-suggestion-meta"> {s.department} · {CADENCE_LABEL[s.cadence]} · <code>{s.measure}</code></span>
                  {!s.available && <div className="kpi-suggestion-reason">{s.reason}</div>}
                </div>
                <button className="btn-secondary btn-small" disabled={!s.available} onClick={() => handleAdopt(s.datasetName, [s.key])}>Add</button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {kpis.length === 0 ? (
        <div className="dataset-empty-state">
          <p>No KPIs yet. Create one from a measure, or add the suggestions above.</p>
        </div>
      ) : groups.map(([dept, list]) => (
        <div className="kpi-group" key={dept}>
          <h3>{dept}</h3>
          <table className="kpi-table">
            <thead>
              <tr><th>Name</th><th>Measure</th><th>Cadence</th><th>Direction</th><th>Target</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {list.map((k) => (
                <tr key={k.id}>
                  <td><strong>{k.name}</strong>{!k.isActive && <span className="dataset-badge warn">inactive</span>}<div className="kpi-key">{k.key}</div></td>
                  <td><code>{k.measure}</code>{k.timeDimension && <div className="kpi-key">by {k.timeDimension}</div>}</td>
                  <td>{CADENCE_LABEL[k.cadence]}</td>
                  <td>{k.direction === 'lower' ? 'Lower is better' : 'Higher is better'}</td>
                  <td>{k.defaultTarget === null ? '—' : formatKpiValue(k.defaultTarget, k.format, k.unit)}{k.targets.length > 1 && <span className="kpi-key"> +{k.targets.length - 1} period override{k.targets.length > 2 ? 's' : ''}</span>}</td>
                  <td className="dataset-actions">
                    <button className="btn-secondary btn-small" onClick={() => navigate(`/kpis/${k.id}`)}>View</button>
                    <button className="btn-secondary btn-small" onClick={() => openEdit(k)}>Edit</button>
                    <button className="btn-danger btn-small" onClick={() => handleDelete(k)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      {editing && (
        <div className="modal-overlay" {...overlay}>
          <div className="modal-content kpi-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editing.id ? 'Edit KPI' : 'New KPI'}</h2>
              <button onClick={() => setEditing(null)} className="modal-close" aria-label="Close">&times;</button>
            </div>
            <form onSubmit={handleSave} className="report-builder-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="kpi-name">Name *</label>
                  <input id="kpi-name" type="text" value={editing.name} required onChange={(e) => update({ name: e.target.value, key: editing.id ? editing.key : slugify(e.target.value) })} />
                </div>
                <div className="form-group">
                  <label htmlFor="kpi-key">Key</label>
                  <input id="kpi-key" type="text" value={editing.key} onChange={(e) => update({ key: e.target.value })} placeholder="auto from name" />
                </div>
                <div className="form-group">
                  <label htmlFor="kpi-department">Department</label>
                  <input id="kpi-department" type="text" value={editing.department} onChange={(e) => update({ department: e.target.value })} list="kpi-departments" />
                  <datalist id="kpi-departments">
                    {[...new Set(kpis.map((k) => k.department).filter(Boolean))].map((d) => <option key={d} value={d} />)}
                  </datalist>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="kpi-measure">Measure *</label>
                  <select id="kpi-measure" value={editing.measure} required onChange={(e) => update({ measure: e.target.value, timeDimension: '' })}>
                    <option value="">— choose a measure —</option>
                    {cubes.map((c) => (
                      <optgroup key={c.name} label={c.title || c.name}>
                        {c.measures.map((m) => <option key={m.name} value={m.name}>{memberLabel(m)} ({m.name.split('.').pop()})</option>)}
                      </optgroup>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="kpi-time-dimension">Time dimension</label>
                  <select id="kpi-time-dimension" value={editing.timeDimension} onChange={(e) => update({ timeDimension: e.target.value })} disabled={!cube}>
                    <option value="">— none (point in time) —</option>
                    {timeDims.map((d) => <option key={d.name} value={d.name}>{memberLabel(d)}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="kpi-cadence">Cadence</label>
                  <select id="kpi-cadence" value={editing.cadence} onChange={(e) => update({ cadence: e.target.value })}>
                    {CADENCES.map((c) => <option key={c} value={c}>{CADENCE_LABEL[c]}</option>)}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="kpi-direction">Direction</label>
                  <select id="kpi-direction" value={editing.direction} onChange={(e) => update({ direction: e.target.value })}>
                    <option value="higher">Higher is better</option>
                    <option value="lower">Lower is better</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="kpi-format">Format</label>
                  <select id="kpi-format" value={editing.format} onChange={(e) => update({ format: e.target.value })}>
                    {FORMATS.map((f) => <option key={f} value={f}>{f}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="kpi-unit">Unit</label>
                  <input id="kpi-unit" type="text" value={editing.unit} onChange={(e) => update({ unit: e.target.value })} placeholder="e.g. h, days" />
                </div>
                <div className="form-group">
                  <label htmlFor="kpi-timing">Timing</label>
                  <select id="kpi-timing" value={editing.timing} onChange={(e) => update({ timing: e.target.value })}>
                    <option value="">—</option>
                    <option value="leading">Leading</option>
                    <option value="lagging">Lagging</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="kpi-default-target">Default target</label>
                  <input id="kpi-default-target" type="number" step="any" value={editing.defaultTarget} onChange={(e) => update({ defaultTarget: e.target.value })} placeholder={editing.format === 'percent' ? 'e.g. 0.85 for 85%' : ''} />
                  <small>{editing.format === 'percent' ? 'Enter percentages as fractions (0.85 = 85%).' : 'Leave blank for no target.'}</small>
                </div>
                <div className="form-group">
                  <label htmlFor="kpi-description">Description</label>
                  <input id="kpi-description" type="text" value={editing.description} onChange={(e) => update({ description: e.target.value })} />
                </div>
              </div>

              <div className="form-group">
                <label>Period targets <span className="form-section-optional">override the default for specific {editing.cadence}s</span></label>
                {editing.targets.map((t, i) => (
                  <div className="form-row filter-row" key={i}>
                    <input type="date" value={t.periodStart || ''} onChange={(e) => update({ targets: editing.targets.map((x, j) => (j === i ? { ...x, periodStart: e.target.value } : x)) })} />
                    <input type="number" step="any" value={t.target} onChange={(e) => update({ targets: editing.targets.map((x, j) => (j === i ? { ...x, target: e.target.value } : x)) })} placeholder="target" />
                    <button type="button" className="btn-secondary-small" onClick={() => update({ targets: editing.targets.filter((_, j) => j !== i) })} aria-label="Remove target">×</button>
                  </div>
                ))}
                <button type="button" className="btn-secondary-small" onClick={() => update({ targets: [...editing.targets, { periodStart: '', target: '' }] })}>+ Add period target</button>
              </div>

              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={() => setEditing(null)}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={saving || !editing.name || !editing.measure}>{saving ? 'Saving…' : 'Save KPI'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default KpiManagement;
